/**
 * wrapt-ready-made-v2.js — the ready-made box page, version 2 (D104).
 *
 * Claude Design "Ready-Made Product Page v2" (conversion update, 30 Sep 2026),
 * with parts of Build Your Own brought in to lift the order value (Alan):
 *
 *   - the wrap choice, as v1;
 *   - the gamified free-delivery bar, which the box rules drive;
 *   - "Pop in something extra": the builder's own catalogue as a feed of what
 *     still fits, with the builder's category and dietary filters, a quantity
 *     on each, and Quick Look on every product;
 *   - the button, which opens v1's Personalise It pop-up (message, photo,
 *     save-the-date), whose own button adds everything as ONE cart line.
 *
 * The box rules are the builder's (byo-v4-core.js createPacker, config from
 * sections/byo-v4-builder.liquid): the box holds 8 spaces, a Small takes 1, a
 * Medium 2, a Large 3, an Extra Large 4, an Extra Small none (so it fits even
 * a full box); the box's own products take theirs;
 * free standard delivery from £60 (D117). An extra shows only while it fits.
 *
 * The line is the ready-made's own variant, quantity 1, in the shape a Build
 * Your Own box goes in (byo-v4-submit.js), so Giftship's cart transform prices
 * it: the box's price plus the PENCE in `_gs_bundle_prices`, each the
 * component's price times its quantity. The keepsake wrap rides at £0. The
 * contents read "<title> - Quantity: <n>", which the bag and the order email
 * both read.
 *
 * Rules carried over from the BYO build, each of which has cost money when
 * broken: nothing wraps fetch or XHR; nothing dispatches the theme's
 * `shapes:cart:*` events; nothing uses /cart/change.js; the header count
 * follows by itself.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm). Shared
 * pieces (Icon, Layer, DatePicker, UploadStatus) come from window.BYOV4, which
 * byo-v4-ui.js and byo-v4-personalise.js fill; modules are never imported
 * across (byo-v4-core.js explains why).
 */

import { html, render, useState, useRef, useEffect, useLayoutEffect, useMemo } from './byo-v4-vendor.js';

const NS = (window.BYOV4 = window.BYOV4 || {});

/** The customer-facing property keys, exactly as v1 and the orders carry them. */
export const KEYS = {
  wrap: 'Giftbox',
  message: 'Gift Message',
  from: 'From ',
  date: 'Keep Wrapt Until ',
  photo: 'Photo',
};

/* The design's copy, and v1's for the pop-up it keeps. */
const COPY = {
  eyebrow: 'Before it goes in your cart',
  personalise: 'Personalise It',
  banner: 'Add a gift message, a printed photo or a save-the-date sticker. All optional.',
  added: 'Added To Your Cart',
  freeLine: 'Free standard delivery on this order',
  viewCart: 'View Cart',
  keepShopping: 'Keep Shopping',
  adding: 'Adding…',
  soldOut: 'Sold out',
  errMessage: 'Write your message, or switch Gift Message off',
  errPhoto: 'Add your photo, or switch Photo off',
  errDate: 'Pick the day it should be opened',
  errUpload: 'That photo didn’t upload. Try again or pick another.',
  errStock: 'One of your extras just sold out. We’ve taken it out, so check your box and add again.',
  errOther: 'Something went wrong adding your box. Try again.',
  all: 'All',
  fits: (n) => `${n} ${n === 1 ? 'item fits' : 'items fit'} in your box · tap a photo for a closer look`,
  nowFull: 'Your box is now full',
  reach: 'Gets You Free Delivery',
  reachN: (n) => `${n} ${n === 1 ? 'item fits and gets' : 'items fit and each gets'} you free delivery · tap a photo for a closer look`,
  more: (n) => `Show ${n} more`,
  emptyFree: 'Nothing in this filter fits your box right now. Try another category.',
  emptyFull: 'Your box is full. Remove an extra to make room for something else.',
  won: (p) => `Free standard delivery unlocked! You saved ${p}`,
  unreach: (g) => `${g} more across your order for free delivery`,
  close: (g) => `So close! Just ${g} to go`,
  half: (g) => `Halfway there. Add ${g} for free delivery`,
  start: (g) => `Add ${g} for free delivery`,
  cant: 'This box can’t fit enough extras. Add another gift and it counts towards your whole order.',
  quick: 'Quick Look',
  inBox: 'In this box',
  why: 'Why we love it',
  noteExtra: 'Not sold separately. It travels inside your Wrapt box.',
  noRoom: 'No more room in the box',
};

const DIETS = [
  ['vegan', 'Vegan'],
  ['af', 'Alcohol-Free'],
];
const SIZE_WORD = { 'Extra Small': 'Extra small', Small: 'Small', Medium: 'Medium', Large: 'Large', 'Extra Large': 'Extra large' };
const PLUM = '#2a1625';
const LIMITS = { message: 300 };
const MAX_PHOTO = 10 * 1024 * 1024;
const BIG_PHOTO = 3 * 1024 * 1024;
const UPLOAD_STALL_MS = 60000;
const UPLOAD_SETTLE_MS = 120000;
const FEED_TTL = 10 * 60 * 1000;

/** £49, or £46.50: whole pounds without the pence, as the design writes them. */
export function money(pence) {
  const p = Math.round(pence || 0);
  return p % 100 ? '£' + (p / 100).toFixed(2) : '£' + p / 100;
}

const reducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function newKey() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const asList = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string' && v.trim().charAt(0) === '[') {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? p : [];
    } catch (_) {
      return [];
    }
  }
  return [];
};

/* ---- icons: the prototype's own strokes ------------------------------------ */

const Plus = ({ c = PLUM }) =>
  html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke=${c} stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>`;
const Minus = () =>
  html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>`;
const Tick = ({ s = 16 }) =>
  html`<svg width=${s} height=${s} viewBox="0 0 24 24" fill="none" stroke=${PLUM} stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>`;
const Van = () =>
  html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke=${PLUM} stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M2 6h12v10H2zM14 10h4l3 3v3h-7z" /><circle cx="6" cy="18" r="2" fill="#fff" /><circle cx="17" cy="18" r="2" fill="#fff" /></svg>`;
const Cross = () =>
  html`<svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>`;
const Info = () =>
  html`<svg class="rm2-ii" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4M12 8h.01"></path></svg>`;
const Icon = (props) => (NS.Icon ? html`<${NS.Icon} ...${props} />` : null);

/* ---- the box rules ---------------------------------------------------------- */

export function slotsFor(cfg, size) {
  const n = cfg.rules.sizes[size];
  return typeof n === 'number' ? n : null;
}

/** "Small": the size alone. Spaces are the page's own sums and are never
    named to the customer (D111, Alan: spaces are not products). */
export function sizePill(cfg, size) {
  return SIZE_WORD[size] || size;
}

/** The extras chosen, in the order they went in, with their quantity. */
export function chosen(s, byHandle) {
  return Object.keys(s.items)
    .filter((h) => s.items[h] > 0 && byHandle[h])
    .map((h) => ({ x: byHandle[h], q: s.items[h] }));
}

/**
 * The room left (the design's "Rules engine"):
 *   used   = the box's own products' spaces
 *   extras = spaces × quantity of the extras added
 *   free   = capacity − used − extras
 * `full` is the box alone filling it: no room for extras at all. An extra
 * that takes no space (Extra Small, Alan 24 Oct 2026) still goes into a box
 * with none left, so while one is on sale the box is never `full`.
 */
const freeSeen = new WeakMap();
function freeOnSale(byHandle) {
  if (!byHandle || typeof byHandle !== 'object') return false;
  if (!freeSeen.has(byHandle)) freeSeen.set(byHandle, Object.values(byHandle).some((x) => x && x.slots === 0));
  return freeSeen.get(byHandle);
}

export function room(cfg, s, byHandle) {
  const cap = cfg.rules.capacity;
  const used = Math.min(cap, cfg.rules.used);
  const picked = chosen(s, byHandle);
  const extras = picked.reduce((a, { x, q }) => a + x.slots * q, 0);
  return {
    cap,
    used,
    extras,
    free: Math.max(0, cap - used - extras),
    full: cap - used <= 0 && !freeOnSale(byHandle),
    left: sizesLeft(cfg, picked),
  };
}

/**
 * One Extra Large per box (Alan, 24 Oct 2026): never two big bottles
 * together, however many spaces are free. `rules.most` is the limit per size
 * (none set, no limit) and `rules.inside` what the box's own products already
 * hold; the extras chosen count too. Returns how many more of each limited
 * size may go in.
 */
function sizesLeft(cfg, picked) {
  const most = cfg.rules.most || {};
  const inside = cfg.rules.inside || {};
  const left = {};
  Object.keys(most).forEach((size) => {
    if (typeof most[size] !== 'number') return;
    const have = (inside[size] || 0) + picked.reduce((a, { x, q }) => a + (x.size === size ? q : 0), 0);
    left[size] = Math.max(0, most[size] - have);
  });
  return left;
}

/** One more of this extra goes in: the space, and its size's limit. */
export function fitsIn(rm, x) {
  return x.slots <= rm.free && !(rm.left && rm.left[x.size] === 0);
}

/** What counts towards free delivery: the box, its extras, the photo. */
export function total(cfg, s, byHandle) {
  let sum = cfg.product.price;
  chosen(s, byHandle).forEach(({ x, q }) => {
    sum += x.price * q;
  });
  if (s.photo.on && cfg.photo) sum += cfg.photo.price;
  return sum;
}

/**
 * The design's suggestion: from the extras not yet added, the cheapest set
 * whose spaces fit what is left and whose price reaches the gap; a tie goes to
 * fewer items. Since D110 the bar no longer names it (Alan: the picks were not
 * always on theme); it only tells whether the box can reach the line at all.
 * The prototype tries every subset; the builder's catalogue is too
 * large for that, so this is the same answer by a knapsack over (spaces used,
 * price so far), keeping the fewest items for each. Returns the items, or null.
 */
export function bestSet(pool, items, free, gap, left = {}) {
  /* With no space left only an Extra Small can go in, so it still runs. */
  if (!(gap > 0)) return null;
  /* Only as many of one size and price as could ever go in together. */
  const cands = [];
  const seen = new Map();
  pool.forEach((x) => {
    if (items[x.handle] || x.slots > free || left[x.size] === 0) return;
    const k = `${x.slots}|${x.price}`;
    const n = seen.get(k) || 0;
    if (n >= Math.floor(free / x.slots)) return;
    seen.set(k, n + 1);
    cands.push(x);
  });
  let best = null;
  /* A limited size (one Extra Large) is counted in the state too, so the set
     never holds more of it than may go in. */
  const limited = Object.keys(left);
  const states = new Map([['0|0', { slots: 0, price: 0, set: [], n: {} }]]);
  cands.forEach((x, i) => {
    const snapshot = [...states.values()];
    snapshot.forEach((st) => {
      const slots = st.slots + x.slots;
      if (slots > free) return;
      const n = limited.includes(x.size) ? { ...st.n, [x.size]: (st.n[x.size] || 0) + 1 } : st.n;
      if (limited.includes(x.size) && n[x.size] > left[x.size]) return;
      const price = st.price + x.price;
      if (best && price > best.price) return;
      const set = st.set.concat(i);
      if (price >= gap) {
        if (!best || price < best.price || (price === best.price && set.length < best.set.length)) best = { price, set };
        return;
      }
      const key = `${slots}|${price}|${limited.map((z) => n[z] || 0).join(',')}`;
      const have = states.get(key);
      if (!have || set.length < have.set.length) states.set(key, { slots, price, set, n });
    });
  });
  return best ? best.set.map((i) => cands[i]) : null;
}

/**
 * Whether the box itself can ever reach free delivery (D153): its price, a
 * printed photo and the best set of extras that fits the spaces it has left.
 * When it cannot, the page offers no bar and no "Gets You Free Delivery"
 * (Alan: don't push customers towards a line they can't reach). The section's
 * first paint makes the same call from a ceiling, `rules.reach`.
 */
export function canReach(cfg, pool) {
  if (cfg.product.price >= cfg.rules.free) return true;
  const left = cfg.rules.capacity - Math.min(cfg.rules.capacity, cfg.rules.used);
  const gap = cfg.rules.free - cfg.product.price - (cfg.photo ? cfg.photo.price : 0);
  return gap <= 0 || !!bestSet(pool, {}, left, gap, sizesLeft(cfg, []));
}

/* ---- the catalogue: the builder's own feed ------------------------------------- */

function fromAddon(cfg, a) {
  const slots = slotsFor(cfg, a.size);
  if (slots == null || !a.variantId) return null;
  return {
    handle: a.handle,
    variantId: a.variantId,
    title: a.title,
    vendor: a.vendor || '',
    price: a.price,
    size: a.size,
    slots,
    diet: asList(a.diet),
    alcohol: !!a.alcohol,
    cats: asList(a.cats).filter(Boolean),
    img: a.img || null,
    images: [],
    desc: null,
    brand: null,
    addon: true,
  };
}

/** One product of collection.byo-v4's feed, as byo-v4-core.js normaliseProduct
    reads it: dropped when unavailable or without a size. */
function fromFeed(cfg, raw, col) {
  if (!raw || !raw.available || !raw.variantId) return null;
  const slots = slotsFor(cfg, raw.sizeRaw);
  if (slots == null) return null;
  if (raw.handle === cfg.product.handle) return null;
  const diet = asList(raw.dietaries);
  return {
    handle: raw.handle,
    variantId: raw.variantId,
    title: raw.title,
    vendor: raw.vendor || '',
    price: raw.price,
    size: raw.sizeRaw,
    slots,
    diet,
    alcohol: col === cfg.alcoholCol && !diet.includes('Alcohol free'),
    cats: cfg.chips.filter((c) => c.cols.includes(col)).map((c) => c.key),
    img: raw.img || null,
    images: Array.isArray(raw.images) ? raw.images : [],
    desc: raw.desc || null,
    brand: raw.details && raw.details.brand ? String(raw.details.brand) : null,
    addon: false,
  };
}

/** The box's own add-ons first, then the catalogue in the filters' order, once
    each, a product in two collections under both filters. */
export function mergePool(cfg, lists) {
  const byHandle = new Map();
  const order = [];
  const put = (e) => {
    if (!e) return;
    const have = byHandle.get(e.handle);
    if (!have) {
      byHandle.set(e.handle, e);
      order.push(e.handle);
      return;
    }
    e.cats.forEach((k) => {
      if (!have.cats.includes(k)) have.cats.push(k);
    });
    have.alcohol = (have.alcohol || e.alcohol) && !have.diet.includes('Alcohol free');
    if (!have.images.length && e.images.length) have.images = e.images;
    if (!have.desc && e.desc) have.desc = e.desc;
    if (!have.brand && e.brand) have.brand = e.brand;
    if (!have.img && e.img) have.img = e.img;
  };
  (cfg.addons || []).forEach((a) => put(fromAddon(cfg, a)));
  (lists || []).forEach(({ col, products }) => (products || []).forEach((raw) => put(fromFeed(cfg, raw, col))));
  return order.map((h) => byHandle.get(h));
}

/* No `Accept: application/json` on these: with it Shopify answers with its own
   collection or product JSON and ignores ?view= (byo-v4-fill.js found the
   same). */
async function fetchFeed(cfg, col) {
  const products = [];
  let url = `${cfg.routes.root}collections/${encodeURIComponent(col)}?view=byo-v4`;
  for (let page = 0; url && page < 10; page += 1) {
    const res = await fetch(url);
    if (!res.ok) break;
    let json;
    try {
      json = await res.json();
    } catch (_) {
      break;
    }
    (json.products || []).forEach((p) => products.push(p));
    const next = json.nextUrl;
    url = next ? (/[?&]view=/.test(next) ? next : `${next}${next.includes('?') ? '&' : '?'}view=byo-v4`) : null;
  }
  return { col, products };
}

/** The catalogue, kept for ten minutes in the tab so the next box opens with it. */
export async function loadPool(cfg) {
  const cols = [];
  cfg.chips.forEach((c) =>
    c.cols.forEach((h) => {
      if (!cols.includes(h)) cols.push(h);
    })
  );
  const key = `wrapt-rm2-feed:v2:${cols.join(',')}`;
  let lists = null;
  try {
    const hit = JSON.parse(window.sessionStorage.getItem(key) || 'null');
    if (hit && Date.now() - hit.at < FEED_TTL) lists = hit.lists;
  } catch (_) {
    lists = null;
  }
  if (!lists) {
    lists = await Promise.all(cols.map((c) => fetchFeed(cfg, c).catch(() => ({ col: c, products: [] }))));
    if (lists.every((l) => l.products.length)) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), lists }));
      } catch (_) {
        /* a full or blocked storage only costs the next page a fetch */
      }
    }
  }
  return mergePool(cfg, lists);
}

/* Quick Look's details, one product at a time (templates/product.wrapt-look.liquid). */
const LOOKS = new Map();
export function lookDetails(cfg, handle) {
  if (!LOOKS.has(handle)) {
    const job = fetch(`${cfg.routes.root}products/${encodeURIComponent(handle)}?view=wrapt-look`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((d) => {
        if (!d) LOOKS.delete(handle);
        return d;
      });
    LOOKS.set(handle, job);
  }
  return LOOKS.get(handle);
}

/* ---- the line ------------------------------------------------------------------ */

export function blankBox(cfg) {
  const first = cfg && cfg.wraps ? cfg.wraps.findIndex((w) => w.available) : -1;
  return {
    box: first >= 0 ? first : null,
    items: {},
    cat: 'all',
    diet: [],
    reach: false,
    all: false,
    message: { on: false, text: '' },
    photo: { on: false, file: null },
    date: { on: false, value: '' },
  };
}

/** Every toggle that is on needs its input (v1). */
export function checks(s) {
  const messageOk = !s.message.on || s.message.text.trim().length > 0;
  const photoOk = !s.photo.on || !!(s.photo.file && s.photo.file.url);
  const dateOk = !s.date.on || !!s.date.value;
  return { messageOk, photoOk, dateOk, ok: messageOk && photoOk && dateOk };
}

/**
 * The line's properties, as `{ scalars, arrays }`: v1's, with a quantity on
 * each extra. `_rm_key` keeps every box its own line and marks it a ready-made
 * for the bag, the multi-address checkout and the email (D91); `_rm_page`
 * records that this page made it, so v2's orders can be told from v1's.
 */
export function buildLine(cfg, s, byHandle, key) {
  const scalars = {};
  const item = {};
  const visible = (k, v) => {
    scalars[k] = v;
    item[k] = v;
  };

  const wrap = s.box == null ? null : cfg.wraps[s.box] || null;
  if (wrap) visible(KEYS.wrap, wrap.name);
  if (s.message.on && s.message.text.trim()) {
    visible(KEYS.message, s.message.text);
  }
  if (s.photo.on && s.photo.file && s.photo.file.url) visible(KEYS.photo, s.photo.file.url);
  if (s.date.on && s.date.value) visible(KEYS.date, s.date.value);
  scalars._rm_key = key;
  scalars._rm_page = 'v2';

  const arrays = { _gs_bundle_ids: [], _gs_bundle_quantities: [], _gs_bundle_prices: [], _gs_bundle_contents: [] };
  const component = (variantId, qty, unit, title) => {
    arrays._gs_bundle_ids.push(String(variantId));
    arrays._gs_bundle_quantities.push(String(qty));
    arrays._gs_bundle_prices.push(String(unit * qty));
    arrays._gs_bundle_contents.push(`${title} - Quantity: ${qty}`);
  };
  if (wrap && wrap.variantId) component(wrap.variantId, 1, 0, `Keepsake Box - ${wrap.name}`);
  chosen(s, byHandle).forEach(({ x, q }) => component(x.variantId, q, x.price, x.title));
  if (s.photo.on && cfg.photo) component(cfg.photo.variantId, 1, cfg.photo.price, 'Printed Keepsake Photo');

  if (!arrays._gs_bundle_ids.length) return { scalars, arrays: {} };
  Object.assign(scalars, {
    _gs_bundle_key: key,
    _gs_bundle_discount: '0',
    _gs_bundle_currency: (cfg.currency || 'GBP').toUpperCase(),
    _gs_bundle_rate: '1.0',
    _gs_item_properties: JSON.stringify(item),
  });
  return { scalars, arrays };
}

/** Multipart: scalars as `properties[k]`, arrays as repeated `properties[k][]`. */
export function formOf(variantId, { scalars, arrays }) {
  const fd = new FormData();
  fd.append('id', String(variantId));
  fd.append('quantity', '1');
  Object.keys(scalars).forEach((k) => fd.append(`properties[${k}]`, scalars[k]));
  Object.keys(arrays).forEach((k) => arrays[k].forEach((v) => fd.append(`properties[${k}][]`, v)));
  return fd;
}

/**
 * Upload a photo and return its CDN URL: v1's, which is byo-v4-fill.js's
 * `uploadPhoto` (a temporary line on the builder's base variant carries the
 * file; the URL is read back and the line taken out with update.js). An
 * XMLHttpRequest because fetch has no upload progress (D55).
 */
export async function uploadPhoto(cfg, file, onProgress) {
  const variantId = cfg.baseVariantId;
  if (!variantId) throw new Error('no base variant to upload against');
  const root = cfg.routes.root;
  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  body.append('properties[Photo]', file, file.name);
  body.append('properties[_tempUpload]', 'true');
  const report = typeof onProgress === 'function' ? onProgress : () => {};

  const dropTemp = async (keys) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        let doomed = keys;
        if (!doomed) {
          const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) continue;
          const cart = await res.json();
          doomed = ((cart && cart.items) || [])
            .filter((it) => it.properties && String(it.properties._tempUpload) === 'true')
            .map((it) => it.key);
          if (!doomed.length) return;
        }
        const updates = {};
        doomed.forEach((k) => {
          updates[k] = 0;
        });
        const gone = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (gone.ok) return;
      } catch (_) {
        /* try again */
      }
    }
  };

  let sent = false;
  let json;
  try {
    json = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let timer = 0;
      let stalled = '';
      const arm = (ms, why) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          stalled = why;
          xhr.abort();
        }, ms);
      };
      const finish = () => {
        if (sent) return;
        sent = true;
        arm(UPLOAD_SETTLE_MS, 'had no answer');
        report({ phase: 'finishing', pct: 100 });
      };
      xhr.open('POST', `${root}cart/add.js`);
      xhr.setRequestHeader('X-Giftship-Drawer-Ignore', 'true');
      xhr.upload.onprogress = (e) => {
        if (!sent) arm(UPLOAD_STALL_MS, 'stalled');
        if (!e.lengthComputable || !e.total) return;
        if (e.loaded >= e.total) return finish();
        report({ phase: 'sending', pct: Math.min(99, Math.floor((e.loaded / e.total) * 100)) });
      };
      xhr.upload.onload = finish;
      xhr.onload = () => {
        clearTimeout(timer);
        if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(`upload responded ${xhr.status}`));
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          reject(e);
        }
      };
      xhr.onerror = () => {
        clearTimeout(timer);
        reject(new Error('upload failed on the network'));
      };
      xhr.onabort = () => {
        clearTimeout(timer);
        reject(new Error(stalled ? `upload ${stalled}` : 'upload aborted'));
      };
      arm(UPLOAD_STALL_MS, 'stalled');
      xhr.send(body);
    });
  } catch (e) {
    if (sent) await dropTemp(null);
    throw e;
  }
  const line = (json && json.items && json.items[0]) || json;
  const url = line && line.properties && line.properties.Photo;
  if (line && line.key) await dropTemp([line.key]);
  if (!url) throw new Error('upload returned no url');
  return url;
}

/** Add the box: one POST to /cart/add.js (v1). */
export async function submitBox(cfg, s, byHandle) {
  const key = newKey();
  let res;
  try {
    res = await fetch(`${cfg.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Giftship-Drawer-Ignore': 'true' },
      body: formOf(cfg.product.variantId, buildLine(cfg, s, byHandle, key)),
    });
  } catch (_) {
    return { ok: false, message: '' };
  }
  if (res.ok) return { ok: true, key };
  let message = '';
  try {
    const json = await res.json();
    message = json.description || json.message || '';
  } catch (_) {
    message = '';
  }
  const hit = chosen(s, byHandle).find(({ x }) => message && message.includes(x.title));
  return { ok: false, status: res.status, message, extra: hit ? hit.x : null };
}

/** The chosen extras no longer in stock, asked of the storefront (v1). */
async function soldOutExtras(cfg, s, byHandle) {
  const answers = await Promise.all(
    chosen(s, byHandle).map(async ({ x }) => {
      try {
        const res = await fetch(`${cfg.routes.root}products/${x.handle}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return x;
        const p = await res.json();
        const v = (p.variants || []).find((pv) => String(pv.id) === String(x.variantId));
        return v && v.available ? null : x;
      } catch (_) {
        return null;
      }
    })
  );
  return answers.filter(Boolean);
}

/** The header bag's 500ms pulse (v1). */
function pulseBag() {
  if (reducedMotion()) return;
  const bag = document.querySelector('header a[href$="/cart"]');
  const target = bag && (bag.querySelector('svg') || bag);
  if (target && target.animate) {
    target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], {
      duration: 500,
      easing: 'cubic-bezier(.3,1.6,.5,1)',
    });
  }
}

/** Focus stays inside a dialog while it is open (v1). */
function trapTab(e, root) {
  if (e.key !== 'Tab' || !root) return;
  const f = [...root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, summary, [tabindex]:not([tabindex="-1"])')].filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
  if (!f.length) return;
  const first = f[0];
  const last = f[f.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/** Render into a node the page already has, from inside the one app. */
function Into({ node, children }) {
  useLayoutEffect(() => {
    if (node) render(children, node);
  });
  useLayoutEffect(
    () => () => {
      if (node) render(null, node);
    },
    [node]
  );
  return null;
}

/* ---- the page's parts ---------------------------------------------------------- */

function Wraps({ cfg, s, set }) {
  if (!cfg.wraps.length) return null;
  /* The heading alone: the chosen tile shows the choice (D115, Alan; the
     design named it on the right as well). */
  return html`
    <div class="rm2-sub" id="rm2-wrap-h" key="wrap-h">${cfg.copy.boxHeading}</div>
    <div class="rm2-wraps" role="radiogroup" aria-labelledby="rm2-wrap-h" key="wraps">
      ${cfg.wraps.map((w, i) => {
        const on = s.box === i;
        return html`<button
          type="button"
          key=${w.name}
          class=${`rm2-wo${on ? ' is-on' : ''}`}
          role="radio"
          aria-checked=${on ? 'true' : 'false'}
          disabled=${!w.available}
          onClick=${() => set((x) => ({ ...x, box: i }))}
        >
          ${w.img ? html`<img src=${w.img} alt="" loading="lazy" />` : null}
          <b>${w.name}</b>
        </button>`;
      })}
    </div>
  `;
}

/** The free-delivery bar (the design's "Rules engine & free delivery"). */
function FreeBar({ cfg, t, rm, sg, pending, reach }) {
  const ref = useRef(null);
  const prev = useRef(null);
  const FREE = cfg.rules.free;
  const left = FREE - t;
  const won = left <= 0;
  const pct = Math.min(100, (t / FREE) * 100);
  const cart = !won && !sg && !pending;

  useEffect(() => {
    const f = ref.current;
    const was = prev.current;
    prev.current = t;
    if (!f || was == null || was === t || reducedMotion()) return;
    f.classList.remove('is-bump', 'is-pop');
    void f.offsetWidth;
    f.classList.add(won && was < FREE ? 'is-pop' : 'is-bump');
    if (t > was) {
      const fl = document.createElement('span');
      fl.className = 'rm2-fd-fl';
      fl.style.left = `${pct}%`;
      fl.style.color = PLUM;
      fl.textContent = `+${money(t - was)}`;
      const bar = f.querySelector('.rm2-fd-bar');
      if (bar) bar.appendChild(fl);
      setTimeout(() => fl.remove(), 1000);
    }
    if (won && was < FREE) {
      const b = f.querySelector('.rm2-burst');
      if (b) {
        b.textContent = '';
        for (let i = 0; i < 18; i += 1) {
          const a = (i / 18) * 6.28;
          const d = 50 + Math.random() * 40;
          const sp = document.createElement('span');
          sp.style.setProperty('--x', `${Math.cos(a) * d}px`);
          sp.style.setProperty('--y', `${Math.sin(a) * d}px`);
          sp.style.background = [PLUM, '#ff8b45', '#fff'][i % 3];
          b.appendChild(sp);
        }
      }
    }
  }, [t]);

  if (!won && !sg && rm.full) return null;
  if (!won && !reach) return null;
  const gap = money(left);
  const title = won
    ? COPY.won(money(cfg.rules.saved))
    : cart
    ? COPY.unreach(gap)
    : pct >= 75
    ? COPY.close(gap)
    : pct >= 50
    ? COPY.half(gap)
    : COPY.start(gap);
  return html`
    <div ref=${ref} key="fd" class=${`rm2-fd${won ? ' is-won' : ''}${cart ? ' is-cart' : ''}`} aria-live="polite">
      <div class="rm2-fd-t"><span class="rm2-fd-tt">${title}</span><span>${money(Math.min(t, FREE))} of ${money(FREE)}</span></div>
      <div class="rm2-fd-bar">
        <i style=${`width:${pct}%`}></i><span class="rm2-fd-end">Free</span><span class="rm2-fd-van" style=${`left:${pct}%`}><${Van} /></span>
      </div>
      <div class="rm2-burst"></div>
      ${cart ? html`<div class="rm2-fd-sg"><span>${COPY.cant}</span></div>` : null}
    </div>
  `;
}

function Chips({ cfg, s, set, reach }) {
  const pick = (cat) => set((x) => ({ ...x, cat, all: false }));
  return html`
    <div class="rm2-chips" role="group" aria-label="Filter the extras">
      ${reach
        ? html`<button
              type="button"
              key="reach"
              class=${`rm2-dt${s.reach ? ' is-on' : ''}`}
              aria-pressed=${s.reach ? 'true' : 'false'}
              onClick=${() => set((x) => ({ ...x, reach: !x.reach, all: false }))}
            >
              ${s.reach ? html`<${Tick} s=${12} />` : null}${COPY.reach}
            </button>
            <hr key="reach-hr" />`
        : null}
      <button type="button" class=${s.cat === 'all' ? 'is-on' : ''} aria-pressed=${s.cat === 'all' ? 'true' : 'false'} onClick=${() => pick('all')}>${COPY.all}</button>
      ${cfg.chips.map(
        (c) =>
          html`<button type="button" key=${c.key} class=${s.cat === c.key ? 'is-on' : ''} aria-pressed=${s.cat === c.key ? 'true' : 'false'} onClick=${() => pick(c.key)}>
            ${c.label}
          </button>`
      )}
      <hr />
      ${DIETS.map(([k, label]) => {
        const on = s.diet.includes(k);
        return html`<button
          type="button"
          key=${k}
          class=${`rm2-dt${on ? ' is-on' : ''}`}
          aria-pressed=${on ? 'true' : 'false'}
          onClick=${() => set((x) => ({ ...x, diet: on ? x.diet.filter((d) => d !== k) : [...x.diet, k], all: false }))}
        >
          ${on ? html`<${Tick} s=${12} />` : null}${label}
        </button>`;
      })}
    </div>
  `;
}

function Card({ x, q, fitsMore, onLook, onAdd, onDec }) {
  const on = q > 0;
  return html`
    <div class=${`rm2-pc${on ? ' is-on' : ''}`} data-rm2-h=${x.handle}>
      <span class="rm2-pi">
        ${x.img ? html`<img src=${x.img} alt="" loading="lazy" />` : null}
        <button type="button" class="rm2-pi-look" aria-label=${`A closer look at ${x.title}`} onClick=${(e) => onLook('extra', x.handle, e.currentTarget)}></button>
        ${on
          ? html`<span class="rm2-added"><${Tick} s=${12} />${q > 1 ? `${q} added` : 'Added'}</span>
              <span class="rm2-qty">
                <button type="button" aria-label=${q === 1 ? `Remove ${x.title}` : `One fewer ${x.title}`} onClick=${() => onDec(x.handle)}><${Minus} /></button>
                <b aria-live="polite">${q}</b>
                <button
                  type="button"
                  aria-label=${`Add another ${x.title}`}
                  disabled=${!fitsMore}
                  title=${fitsMore ? undefined : COPY.noRoom}
                  onClick=${() => onAdd(x.handle)}
                >
                  <${Plus} c="#fff" />
                </button>
              </span>`
          : html`<button type="button" class="rm2-pa" aria-label=${`Add ${x.title}`} onClick=${() => onAdd(x.handle)}><${Plus} /></button>`}
      </span>
      <b>${x.title}</b>
      <small>+${money(x.price)}${q > 1 ? ' each' : ''}</small>
    </div>
  `;
}

/* ---- Quick Look (the design's, for extras and "What's inside") --------------------- */

function QuickLook({ cfg, ql, s, byHandle, rm, det, onClose, onAdd, onDec }) {
  const dialog = useRef(null);
  const closeBtn = useRef(null);
  const [qi, setQi] = useState(0);
  const extra = ql.kind === 'extra';
  const base = extra ? byHandle[ql.handle] : cfg.contents.find((c) => c.handle === ql.handle);

  useEffect(() => {
    setQi(0);
  }, [ql.handle]);
  useEffect(() => {
    if (ql.phase !== 'on') return;
    const d = dialog.current;
    if (d && !d.contains(document.activeElement) && closeBtn.current) closeBtn.current.focus({ preventScroll: true });
  }, [ql.phase]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      trapTab(e, dialog.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (!base) return null;
  const images = det && det.images && det.images.length ? det.images : base.images && base.images.length ? base.images : base.img ? [base.img] : [];
  const i = Math.min(qi, Math.max(0, images.length - 1));
  const q = extra ? s.items[base.handle] || 0 : 0;
  const fits = extra && fitsIn(rm, base);
  const diet = asList(base.diet);
  const vendor = (det && det.vendor) || base.vendor || '';
  const descHtml = det && det.desc ? det.desc : null;
  const descText = !descHtml && base.desc ? base.desc : null;
  const why = cfg.flags.why && det && det.why ? String(det.why).trim() : '';
  const brand = (det && det.brand) || base.brand || '';
  const on = ql.phase === 'on';

  const gallery = html`<div>
    <div class="rm2-ql-g">
      ${images.length ? html`<img src=${images[i]} alt=${base.title} />` : null}
      ${images.length > 1 ? html`<span class="rm2-gcount">${i + 1} / ${images.length}</span>` : null}
    </div>
    ${images.length > 1
      ? html`<div class="rm2-ql-th">
          ${images.map(
            (u, k) =>
              html`<button type="button" key=${u} class=${k === i ? 'is-on' : ''} aria-label=${`Image ${k + 1}`} aria-current=${k === i ? 'true' : 'false'} onClick=${() => setQi(k)}>
                <img src=${u.replace(/([?&])width=\d+/, '$1width=120')} alt="" />
              </button>`
          )}
        </div>`
      : null}
  </div>`;

  const info = html`<div class="rm2-ql-i">
    <div class="rm2-ql-top">
      <span>${extra ? COPY.quick : COPY.inBox}</span>
      <button type="button" class="rm2-ql-x" aria-label="Close" ref=${closeBtn} onClick=${onClose}><${Cross} /></button>
    </div>
    ${vendor ? html`<div class="rm2-ql-v">${vendor}</div>` : null}
    <h2 class="rm2-d">${base.title}</h2>
    ${extra ? html`<div class="rm2-ql-p">+${money(base.price)}</div>` : null}
    <div class="rm2-pills">
      ${base.size && slotsFor(cfg, base.size) != null ? html`<span class="rm2-pl">${sizePill(cfg, base.size)}</span>` : null}
      ${diet.includes('Vegan') ? html`<span class="rm2-pl rm2-pl--lime">Vegan</span>` : null}
      ${base.alcohol ? html`<span class="rm2-pl rm2-pl--ol">Contains Alcohol</span>` : null}
    </div>
    ${descHtml
      ? html`<div class="rm2-ql-d" dangerouslySetInnerHTML=${{ __html: descHtml }}></div>`
      : descText
      ? html`<div class="rm2-ql-d"><p>${descText}</p></div>`
      : null}
    ${why ? html`<div class="rm2-wl"><b>${COPY.why}</b><p>${why}</p></div>` : null}
    ${brand
      ? html`<div>
          <details>
            <summary>About ${vendor || 'the brand'}</summary>
            <p>${brand}</p>
          </details>
        </div>`
      : null}
    <div class="rm2-ql-note">${extra ? COPY.noteExtra : `Part of ${cfg.product.name}. Not sold separately.`}</div>
  </div>`;

  const foot = extra
    ? html`<div class="rm2-ql-f">
        <div>
          ${q ? `${q} in your box` : fits ? 'Fits in your box' : 'Won’t fit right now'}
          ${q || fits ? null : html`<small>Remove an extra to make room</small>`}
        </div>
        ${q
          ? html`<span class="rm2-qty">
              <button type="button" aria-label="One fewer" onClick=${() => onDec(base.handle)}><${Minus} /></button>
              <b aria-live="polite">${q}</b>
              <button type="button" aria-label="Add another" disabled=${!fits} onClick=${() => onAdd(base.handle)}><${Plus} c="#fff" /></button>
            </span>`
          : html`<button
              type="button"
              class="rm2-btn"
              disabled=${!fits}
              onClick=${() => {
                onAdd(base.handle);
                onClose();
              }}
            >
              <${Plus} c="#fff" />Add To Box
            </button>`}
      </div>`
    : html`<div class="rm2-ql-f">
        <div>Already in your box<small>Included in the ${money(cfg.product.price)}</small></div>
        <button type="button" class="rm2-btn rm2-btn--ol" onClick=${onClose}>Close</button>
      </div>`;

  return html`<div class="rm2-layer">
    <div class=${`rm2-ql-bd${on ? ' is-on' : ''}`} onClick=${onClose}></div>
    <div class=${`rm2-ql${on ? ' is-on' : ''}`} role="dialog" aria-modal="true" aria-label=${base.title} ref=${dialog}>
      <span class="rm2-ql-grab"></span>
      <div class="rm2-ql-b">${gallery}${info}</div>
      ${foot}
    </div>
  </div>`;
}

/* ---- Personalise It (v1's pop-up, with a quantity on the extras) -------------------- */

function TouchRow({ id, icon, title, sub, on, err, onToggle, children }) {
  return html`
    <div class=${`wrapt-rmp-touch${on ? ' is-on' : ''}${err ? ' is-err' : ''}`} data-touch=${id}>
      <div class="wrapt-rmp-touch__head">
        <span class="wrapt-rmp-touch__icon" aria-hidden="true"><${Icon} name=${icon} size=${20} /></span>
        <span class="wrapt-rmp-touch__label">
          <span class="wrapt-rmp-touch__title">${title}</span>
          <span class="wrapt-rmp-touch__sub">${sub}</span>
        </span>
        <button
          type="button"
          role="switch"
          aria-checked=${on ? 'true' : 'false'}
          aria-label=${title}
          class=${`wrapt-rmp-switch${on ? ' is-on' : ''}`}
          onClick=${() => onToggle(!on)}
        >
          <span class="wrapt-rmp-switch__knob"></span>
        </button>
      </div>
      ${on && html`<div class="wrapt-rmp-touch__body">${children}</div>`}
    </div>
  `;
}

const ErrLine = ({ children }) =>
  children ? html`<p class="wrapt-rmp-err" role="alert"><span class="wrapt-rmp-err__dot" aria-hidden="true"></span>${children}</p>` : null;

function PhotoControl({ s, upload, uploadErr, onFile }) {
  const input = useRef(null);
  const pick = () => input.current && input.current.click();
  return html`
    <div class="wrapt-rmp-photo">
      <input
        ref=${input}
        class="wrapt-rmp-photo__file"
        type="file"
        accept="image/jpeg,image/png"
        onChange=${(e) => {
          const el = e.target;
          onFile(el.files && el.files[0]);
          el.value = '';
        }}
      />
      ${upload
        ? html`<${NS.UploadStatus}
            upload=${upload}
            say=${upload.phase === 'finishing' ? 'Nearly there — saving your photo…' : `Uploading your photo… ${upload.pct}%`}
          />`
        : s.photo.file
        ? html`<button type="button" class="wrapt-rmp-photo__done" onClick=${pick}>
            <img src=${s.photo.file.url} alt="" />
            <span class="wrapt-rmp-photo__meta">
              <span class="wrapt-rmp-photo__name">${s.photo.file.name}</span>
              <span class="wrapt-rmp-photo__say">Uploaded · tap to replace</span>
            </span>
          </button>`
        : html`<button type="button" class="wrapt-rmp-photo__drop" onClick=${pick}>
            <span class="wrapt-rmp-photo__t">Upload A Photo</span>
            <span class="wrapt-rmp-photo__s">JPG or PNG, up to 10MB</span>
          </button>`}
      <p class="wrapt-rmp-live" aria-live="polite">${upload ? 'Uploading your photo…' : ''}</p>
      <${ErrLine}>${uploadErr}<//>
    </div>
  `;
}

/** The pop-up's "Pop in something extra": v1's cards, for the first four of the
    page's extras that still fit, the box's own add-ons first. */
function PopUpsell({ cfg, s, pool, rm, t, onToggle }) {
  const picks = pool.filter((x) => (s.items[x.handle] || 0) > 0 || fitsIn(rm, x)).slice(0, 4);
  if (!picks.length) return null;
  const thr = cfg.rules.free;
  return html`
    <div class="wrapt-rmp-up">
      <div class="wrapt-rmp__head">
        <div class="wrapt-rmp__h" role="heading" aria-level="3">${cfg.copy.upsellHeading}</div>
        ${cfg.copy.upsellAside ? html`<span class="wrapt-rmp__aside wrapt-rmp__aside--small">${cfg.copy.upsellAside}</span>` : null}
      </div>
      <div class="wrapt-rmp-up__grid" role="list">
        ${picks.map((x) => {
          const on = (s.items[x.handle] || 0) > 0;
          const tips = !on && t < thr && t + x.price >= thr;
          return html`
            <div class="wrapt-rmp-up__cell" role="listitem" key=${x.handle}>
              <button
                type="button"
                class=${`wrapt-rmp-card${on ? ' is-on' : ''}`}
                aria-pressed=${on ? 'true' : 'false'}
                aria-label=${`${x.title}, +${money(x.price)}${tips ? ', gets you free delivery' : ''}`}
                onClick=${() => onToggle(x.handle)}
              >
                <span class="wrapt-rmp-card__img">
                  ${x.img ? html`<img src=${x.img} alt="" loading="lazy" />` : null}
                  ${tips && html`<span class="wrapt-rmp-card__tag">Free delivery</span>`}
                  <span class="wrapt-rmp-card__btn" aria-hidden="true"><${Icon} name=${on ? 'check' : 'plus'} size=${16} stroke=${2.5} /></span>
                </span>
                <span class="wrapt-rmp-card__name">${x.title}</span>
                <span class="wrapt-rmp-card__price">+${money(x.price)}</span>
              </button>
            </div>
          `;
        })}
      </div>
    </div>
  `;
}

function Pop({ cfg, s, t, pool, rm, byHandle, view, busy, tried, fail, upload, uploadErr, added, set, onToggleExtra, onFile, onAdd, onClose, onKeepShopping, ask, onAsk }) {
  const dialog = useRef(null);
  const closeBtn = useRef(null);
  const askCard = useRef(null);
  const c = checks(s);

  useEffect(() => {
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = 'hidden';
    root.classList.add('wrapt-rmp-open');
    return () => {
      root.style.overflow = overflow;
      root.classList.remove('wrapt-rmp-open');
    };
  }, []);
  useEffect(() => {
    requestAnimationFrame(() => {
      const d = dialog.current;
      if (d && !d.contains(document.activeElement) && closeBtn.current) closeBtn.current.focus();
    });
  }, [view]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && ask) onAsk(null);
      else if (e.key === 'Escape' && !busy) onClose();
      trapTab(e, ask ? askCard.current : dialog.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, view, ask]);
  /* The last question takes the focus while it is up (D121). */
  useEffect(() => {
    if (!ask) return;
    requestAnimationFrame(() => {
      const first = askCard.current && askCard.current.querySelector('button');
      if (first) first.focus();
    });
  }, [ask]);

  const focusIn = (key, tries = 8) =>
    requestAnimationFrame(() => {
      const body = dialog.current && dialog.current.querySelector(`[data-touch="${key}"] .wrapt-rmp-touch__body`);
      const field = body && body.querySelector('textarea, input:not([type="file"]), button');
      if (field) field.focus();
      else if (tries > 0) focusIn(key, tries - 1);
    });
  const toggle = (key) => (on) => {
    set((x) => ({ ...x, [key]: { ...x[key], on } }));
    if (on) focusIn(key);
  };
  /* "Add One" / "Add A Message" (D121): Gift Message on, its box in focus. */
  const addMessage = () => {
    onAsk(null);
    toggle('message')(true);
  };

  const units = chosen(s, byHandle).reduce((a, { q }) => a + q, 0);
  const summary = [cfg.product.name, units ? `+ ${units} extra${units === 1 ? '' : 's'}` : null, s.photo.on ? '+ photo' : null].filter(Boolean).join(' ');
  const wrap = s.box == null ? null : cfg.wraps[s.box];

  const header = html`
    <div class="wrapt-rmp-pop__head">
      ${view === 'personalise'
        ? html`${cfg.product.image ? html`<img class="wrapt-rmp-pop__thumb" src=${cfg.product.image} alt="" />` : html`<span class="wrapt-rmp-pop__thumb"></span>`}
            <div class="wrapt-rmp-pop__who">
              <span class="wrapt-rmp-pop__eyebrow">${COPY.eyebrow}</span>
              <span class="wrapt-rmp-pop__name">${cfg.product.name}</span>
              <span class="wrapt-rmp-pop__choice">${[wrap ? wrap.name : '', money(cfg.product.price)].filter(Boolean).join(' · ')}</span>
            </div>`
        : html`<span></span>`}
      <button type="button" class="wrapt-rmp-pop__x" aria-label="Close" ref=${closeBtn} disabled=${busy} onClick=${onClose}>
        <${Icon} name="close" size=${16} stroke=${2.5} />
      </button>
    </div>
  `;

  const personalise = html`
    <div class="wrapt-rmp-pop__body">
      <h2 class="wrapt-rmp-pop__title" id="wrapt-rmp-pop-h">${COPY.personalise}</h2>
      <div class="wrapt-rmp-pop__banner"><${Icon} name="gift" size=${18} /><span><strong>Make it theirs.</strong> ${COPY.banner}</span></div>
      ${fail && html`<p class="wrapt-rmp-pop__fail" role="alert">${fail}</p>`}
      <div class="wrapt-rmp-touches">
        <${TouchRow}
          id="message"
          icon="edit"
          title=${cfg.copy.messageTitle}
          sub=${cfg.copy.messageSub}
          on=${s.message.on}
          err=${tried && !c.messageOk}
          onToggle=${toggle('message')}
        >
          <textarea
            class=${`wrapt-rmp-field wrapt-rmp-field--area${tried && !c.messageOk ? ' is-err' : ''}`}
            rows="4"
            maxlength=${LIMITS.message}
            aria-label="Gift message"
            placeholder="Write something they’ll keep"
            value=${s.message.text}
            onInput=${(e) => set((x) => ({ ...x, message: { ...x.message, text: e.target.value } }))}
          ></textarea>
          <div class="wrapt-rmp-fieldfoot"><span></span><span>${s.message.text.length}/${LIMITS.message}</span></div>
          ${tried && !c.messageOk && html`<${ErrLine}>${COPY.errMessage}<//>`}
        <//>
        ${cfg.photo &&
        html`<${TouchRow}
          id="photo"
          icon="camera"
          title=${cfg.copy.photoTitle}
          sub=${`+${money(cfg.photo.price)} · ${cfg.copy.photoSub}`}
          on=${s.photo.on}
          err=${tried && !c.photoOk && !upload}
          onToggle=${toggle('photo')}
        >
          <${PhotoControl} s=${s} upload=${upload} uploadErr=${uploadErr} onFile=${onFile} />
          ${tried && !c.photoOk && !upload && !uploadErr && html`<${ErrLine}>${COPY.errPhoto}<//>`}
        <//>`}
        ${cfg.flags.date &&
        html`<${TouchRow}
          id="date"
          icon="calendar"
          title=${cfg.copy.dateTitle}
          sub=${cfg.copy.dateSub}
          on=${s.date.on}
          err=${tried && !c.dateOk}
          onToggle=${toggle('date')}
        >
          <${NS.DatePicker}
            value=${s.date.value}
            onPick=${(value) => set((x) => ({ ...x, date: { ...x.date, value } }))}
            config=${{ dateExpressDays: cfg.date.expressDays, dateLeadDays: cfg.date.leadDays }}
          />
          ${tried && !c.dateOk && html`<${ErrLine}>${COPY.errDate}<//>`}
        <//>`}
      </div>
      <${PopUpsell} cfg=${cfg} s=${s} pool=${pool} rm=${rm} t=${t} onToggle=${onToggleExtra} />
    </div>
    <div class="wrapt-rmp-pop__foot">
      ${/* D121 (Alan): going without a message is said plainly, beside the button. */ ''}
      ${!s.message.on &&
      html`<div class="wrapt-rmp-nomsg" role="note">
        <${Icon} name="edit" size=${16} />
        <span><strong>No gift message</strong> It’ll arrive without a message card.</span>
        <button type="button" onClick=${addMessage}>Add One</button>
      </div>`}
      <div class="wrapt-rmp-pop__sum"><span>${summary}</span><strong>${money(t)}</strong></div>
      <button type="button" class="wrapt-rmp-btn" aria-busy=${busy ? 'true' : 'false'} disabled=${busy} onClick=${() => onAdd()}>
        ${busy ? COPY.adding : `${cfg.copy.cta} · ${money(t)}`}
      </button>
    </div>
  `;

  const done = added
    ? html`
        <div class="wrapt-rmp-pop__body wrapt-rmp-added">
          <span class="wrapt-rmp-added__tick" aria-hidden="true"><${Icon} name="check" size=${26} stroke=${3} /></span>
          <h2 class="wrapt-rmp-added__h" id="wrapt-rmp-pop-h">${COPY.added}</h2>
          <ul class="wrapt-rmp-added__list">
            <li>
              ${cfg.product.image ? html`<img src=${cfg.product.image} alt="" />` : html`<span class="wrapt-rmp-added__ph"></span>`}
              <span class="wrapt-rmp-added__what"><strong>${cfg.product.name}</strong><span>${added.boxName}</span></span>
              <span class="wrapt-rmp-added__price">${money(cfg.product.price)}</span>
            </li>
            ${added.extras.map(({ x, q }) => {
              /* The note (none since D116) and the count, whichever there are. */
              const sub = [cfg.copy.upsellAside, q > 1 ? `${q} added` : ''].filter(Boolean).join(' · ');
              return html`<li key=${x.handle}>
                ${x.img ? html`<img src=${x.img} alt="" />` : html`<span class="wrapt-rmp-added__ph"></span>`}
                <span class="wrapt-rmp-added__what"
                  ><strong>${x.title}</strong>${sub ? html`<span>${sub}</span>` : null}</span
                >
                <span class="wrapt-rmp-added__price">+${money(x.price * q)}</span>
              </li>`;
            })}
            ${added.touches.length > 0 &&
            html`<li>
              <span class="wrapt-rmp-added__ph wrapt-rmp-added__ph--icon"><${Icon} name="gift" size=${20} /></span>
              <span class="wrapt-rmp-added__what"><strong>Personalised</strong><span>${added.touches.join(' · ')}</span></span>
              <span class="wrapt-rmp-added__price">${added.photo ? `+${money(cfg.photo.price)}` : ''}</span>
            </li>`}
          </ul>
          ${added.total >= cfg.rules.free && html`<p class="wrapt-rmp-added__free">${COPY.freeLine}</p>`}
          <div class="wrapt-rmp-added__total"><span>Total</span><strong>${money(added.total)}</strong></div>
        </div>
        <div class="wrapt-rmp-pop__foot wrapt-rmp-pop__foot--two">
          <a class="wrapt-rmp-btn" href=${cfg.routes.bag}>${COPY.viewCart}</a>
          <button type="button" class="wrapt-rmp-btn wrapt-rmp-btn--outline" onClick=${onKeepShopping}>${COPY.keepShopping}</button>
        </div>
      `
    : null;

  return html`<${NS.Layer}>
    <div class="wrapt-rmp-pop" data-view=${view}>
      <div class="wrapt-rmp-pop__scrim" onClick=${() => !busy && onClose()}></div>
      <div class="wrapt-rmp-pop__dialog" role="dialog" aria-modal="true" aria-labelledby="wrapt-rmp-pop-h" ref=${dialog}>
        ${header} ${view === 'added' ? done : personalise}
        ${ask &&
        view !== 'added' &&
        html`<div class="wrapt-rmp-ask">
          <div class="wrapt-rmp-ask__scrim" onClick=${() => onAsk(null)}></div>
          <div class="wrapt-rmp-ask__card" role="alertdialog" aria-modal="true" aria-labelledby="wrapt-rmp-ask-h" aria-describedby="wrapt-rmp-ask-p" ref=${askCard}>
            <h3 class="wrapt-rmp-ask__h" id="wrapt-rmp-ask-h">Send it without a message?</h3>
            <p class="wrapt-rmp-ask__p" id="wrapt-rmp-ask-p">There’s no gift message on this box, so it’ll arrive without a message card. Adding one is free.</p>
            <button type="button" class="wrapt-rmp-btn" onClick=${addMessage}>Add A Message</button>
            <button type="button" class="wrapt-rmp-btn wrapt-rmp-btn--outline" onClick=${() => onAsk('go')}>Continue Without A Message</button>
          </div>
        </div>`}
      </div>
    </div>
  <//>`;
}

/* ---- the app ------------------------------------------------------------------- */

function App({ cfg: initial, root, inside, points }) {
  const [cfg, setCfg] = useState(initial);
  const [s, setS] = useState(() => blankBox(initial));
  /* The latest state, kept in step as each change is made (v1): an add pressed
     mid-upload reads the photo the upload has just written. */
  const live = useRef(s);
  const update = (fn) => {
    const next = typeof fn === 'function' ? fn(live.current) : fn;
    live.current = next;
    setS(next);
  };
  const [pool, setPool] = useState(() => mergePool(initial, []));
  const [loaded, setLoaded] = useState(false);
  const [ql, setQl] = useState(null);
  const [looks, setLooks] = useState({});
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  /* The last question's answer: 'go' adds it as it is; anything else closes it. */
  const onAsk = (answer) => {
    setAsk(false);
    if (answer === 'go') onAdd(true);
  };
  const [tried, setTried] = useState(false);
  const [fail, setFail] = useState('');
  const [upload, setUpload] = useState(null);
  const [uploadErr, setUploadErr] = useState('');
  const [added, setAdded] = useState(null);
  const opener = useRef(null);
  const qlOpener = useRef(null);
  const uploading = useRef(null);
  const cta = useRef(null);

  const byHandle = useMemo(() => {
    const m = {};
    pool.forEach((x) => {
      m[x.handle] = x;
    });
    return m;
  }, [pool]);
  const byRef = useRef(byHandle);
  byRef.current = byHandle;

  const rm = room(cfg, s, byHandle);
  const t = total(cfg, s, byHandle);
  const gap = cfg.rules.free - t;
  const sg = useMemo(() => (gap > 0 ? bestSet(pool, s.items, rm.free, gap, rm.left) : null), [pool, s.items, rm.free, gap, JSON.stringify(rm.left)]);
  /* "Gets You Free Delivery" (D109): the extras of which one more fits and, on
     its own, takes the total to the free-delivery line. Offered from the first
     paint while the catalogue loads, then only when one exists. */
  const reachN = gap > 0 && !rm.full ? pool.filter((x) => fitsIn(rm, x) && x.price >= gap).length : 0;
  const reach = useMemo(() => (loaded ? canReach(cfg, pool) : cfg.rules.reach !== false), [loaded, pool]);
  const reachShow = loaded ? reachN > 0 : gap > 0 && !rm.full && reach;
  const reachOn = s.reach && reachN > 0;
  const soldOut = !cfg.product.available;

  useEffect(() => {
    /* Coming soon (D124): nothing to add, so the extras are never fetched. */
    if (cfg.comingSoon) {
      setLoaded(true);
      return undefined;
    }
    let alive = true;
    loadPool(cfg)
      .then((list) => {
        if (!alive) return;
        setPool(list);
        setLoaded(true);
      })
      .catch(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, []);

  /* For the QA driver. */
  useEffect(() => {
    root.__rm2 = { state: () => live.current, pool: () => pool, cfg: () => cfg, loaded };
  });

  const set = (fn) => {
    setFail('');
    update(fn);
  };
  /* Once free delivery is reached, or nothing reaches it, the filter steps aside. */
  useEffect(() => {
    if (loaded && s.reach && !reachN) update((x) => ({ ...x, reach: false }));
  }, [loaded, s.reach, reachN]);
  const addOne = (h) =>
    set((x) => {
      const it = byRef.current[h];
      if (!it || !fitsIn(room(cfg, x, byRef.current), it)) return x;
      return { ...x, items: { ...x.items, [h]: (x.items[h] || 0) + 1 } };
    });
  /* An add from the feed, once the free-delivery panel has scrolled away,
     brings the panel back into view so the new total shows; the feed stays
     open (D120, Alan). Quick Look's adds stay where they are. */
  const addFromFeed = (h) => {
    addOne(h);
    requestAnimationFrame(() => {
      const bar = root.querySelector('.rm2-fd');
      if (!bar) return;
      const head = parseFloat(getComputedStyle(bar).getPropertyValue('--header-height')) || 0;
      if (bar.getBoundingClientRect().top >= head) return;
      bar.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    });
  };
  const decOne = (h) =>
    set((x) => {
      const cur = x.items[h] || 0;
      if (!cur) return x;
      const items = { ...x.items };
      if (cur <= 1) delete items[h];
      else items[h] = cur - 1;
      return { ...x, items };
    });
  /* The pop-up's cards switch an extra in (one) or out (all of it). */
  const toggleExtra = (h) =>
    set((x) => {
      if (x.items[h]) {
        const items = { ...x.items };
        delete items[h];
        return { ...x, items };
      }
      const it = byRef.current[h];
      if (!it || !fitsIn(room(cfg, x, byRef.current), it)) return x;
      return { ...x, items: { ...x.items, [h]: 1 } };
    });

  /* Quick Look opens from a card, a pop-up card or a "What's inside" row. */
  const openLook = (kind, handle, from) => {
    qlOpener.current = from || document.activeElement;
    setQl({ kind, handle, phase: 'in' });
    lookDetails(cfg, handle).then((d) => {
      if (d) setLooks((m) => ({ ...m, [handle]: d }));
    });
    const go = () => setQl((cur) => (cur && cur.handle === handle ? { ...cur, phase: 'on' } : cur));
    if (reducedMotion()) go();
    else requestAnimationFrame(() => requestAnimationFrame(go));
  };
  const closeLook = () => {
    setQl((cur) => (cur ? { ...cur, phase: 'out' } : cur));
    const phone = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    setTimeout(() => setQl((cur) => (cur && cur.phase === 'out' ? null : cur)), reducedMotion() ? 0 : phone ? 380 : 250);
    const back = qlOpener.current;
    if (back && typeof back.focus === 'function' && back.isConnected) requestAnimationFrame(() => back.focus({ preventScroll: true }));
  };
  useEffect(() => {
    const el = document.documentElement;
    if (ql) el.classList.add('wrapt-rmp-open');
    else if (!view) el.classList.remove('wrapt-rmp-open');
  }, [!!ql]);

  /* "What's inside" rows open Quick Look too. Each row is a real button, so
     Enter and Space arrive here as clicks (D107). */
  useEffect(() => {
    const host = root.querySelector('[data-rm2-inside]');
    if (!host) return undefined;
    const go = (e) => {
      const row = e.target.closest('[data-rm2-ql]');
      if (!row || !host.contains(row)) return;
      e.preventDefault();
      openLook('content', row.dataset.rm2Ql, row);
    };
    host.addEventListener('click', go);
    return () => host.removeEventListener('click', go);
  }, []);

  const open = (e) => {
    if (soldOut) return;
    opener.current = (e && e.currentTarget) || document.activeElement;
    setView('personalise');
  };
  const close = () => {
    if (busy) return;
    setView(null);
    setTried(false);
    setAsk(false);
    const back = opener.current;
    if (back && typeof back.focus === 'function') requestAnimationFrame(() => back.focus());
  };

  /* The phone's sticky bar is the section's own markup: its total and button
     are kept in step from here, and it rises once the page's button has
     scrolled above the screen, and goes once it is back (the design). */
  useEffect(() => {
    root.querySelectorAll('[data-rm2-total]').forEach((el) => {
      el.textContent = money(t);
    });
  }, [t]);
  useEffect(() => {
    const btns = [...root.querySelectorAll('[data-rm2-open]')];
    btns.forEach((b) => b.addEventListener('click', open));
    return () => btns.forEach((b) => b.removeEventListener('click', open));
  });
  /* Checked on scroll rather than by an IntersectionObserver alone: a jump
     straight past the button (a #reviews link, a reduced-motion scroll) never
     crosses it, so an observer would not hear of it. */
  useEffect(() => {
    const bar = root.querySelector('[data-rm2-sticky]');
    if (!bar) return undefined;
    let frame = 0;
    const check = () => {
      frame = 0;
      const btn = cta.current;
      if (btn) bar.classList.toggle('is-on', btn.getBoundingClientRect().bottom < 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const onFile = async (file) => {
    if (!file) return;
    setUploadErr('');
    if (!/^image\/(jpeg|png)$/.test(file.type) || file.size > MAX_PHOTO) {
      setUploadErr(COPY.errUpload);
      return;
    }
    const preview = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
    setUpload({ name: file.name, preview, big: file.size > BIG_PHOTO, phase: 'sending', pct: 0 });
    const job = uploadPhoto(cfg, file, ({ phase, pct }) =>
      setUpload((u) => (!u || (u.phase === phase && u.pct === pct) ? u : { ...u, phase, pct }))
    );
    uploading.current = job;
    try {
      const url = await job;
      update((x) => ({ ...x, photo: { ...x.photo, file: { name: file.name, url } } }));
    } catch (_) {
      setUploadErr(COPY.errUpload);
    } finally {
      uploading.current = null;
      setUpload(null);
      if (preview) URL.revokeObjectURL(preview);
    }
  };

  const onAdd = async (sure) => {
    if (busy) return;
    setTried(true);
    setFail('');
    setBusy(true);
    if (uploading.current) {
      try {
        await uploading.current;
      } catch (_) {
        /* the control says so */
      }
    }
    const state = live.current;
    if (!checks(state).ok) {
      setBusy(false);
      requestAnimationFrame(() => {
        const bad = document.querySelector('.wrapt-rmp-pop .wrapt-rmp-touch.is-err');
        if (bad) bad.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      });
      return;
    }
    /* D121 (Alan): a box without a gift message is asked about once more, at
       the very end, so going without one is a choice and never a slip. */
    if (sure !== true && !state.message.on) {
      setBusy(false);
      setAsk(true);
      return;
    }
    const map = byRef.current;
    const r = await submitBox(cfg, state, map);
    setBusy(false);
    if (r.ok) {
      const wrap = state.box == null ? null : cfg.wraps[state.box];
      const touches = [
        state.message.on ? 'Gift message' : null,
        state.photo.on ? 'Printed photo' : null,
        state.date.on ? 'Save-the-date sticker' : null,
      ].filter(Boolean);
      const summary = { boxName: wrap ? wrap.name : '', extras: chosen(state, map), touches, photo: state.photo.on, total: total(cfg, state, map) };
      /* Header menu v2 (12 Oct 2026): the confirmation is the header's popover
         under the bag (assets/wrapt-header.js), and the box starts afresh, as
         Keep Shopping left it. Without the header's script the old view stands. */
      if (window.WraptHeader && typeof window.WraptHeader.added === 'function') {
        const n = summary.extras.reduce((a, e) => a + (e.q || 1), 0);
        const sub = [summary.boxName, n ? `${n} extra${n > 1 ? 's' : ''}` : '', summary.touches.length ? 'Personalised' : ''].filter(Boolean).join(' · ');
        keepShopping();
        window.WraptHeader.added({ name: cfg.product.name, sub, price: money(summary.total), image: cfg.product.image, url: cfg.routes.bag });
        return;
      }
      setAdded(summary);
      setView('added');
      pulseBag();
      return;
    }
    /* A stock refusal names the box, not the extra, so the chosen extras are
       asked again and any sold out are taken out (v1). */
    const out = r.extra ? [r.extra] : r.status === 422 ? await soldOutExtras(cfg, state, map) : [];
    if (out.length) {
      const gone = out.map((x) => x.handle);
      setPool((p) => p.filter((x) => !gone.includes(x.handle)));
      update((x) => {
        const items = { ...x.items };
        gone.forEach((h) => delete items[h]);
        return { ...x, items };
      });
      setFail(COPY.errStock);
      return;
    }
    setFail(COPY.errOther);
  };

  const keepShopping = () => {
    setView(null);
    setAdded(null);
    setTried(false);
    update(blankBox(cfg));
    const back = opener.current;
    if (back && typeof back.focus === 'function') requestAnimationFrame(() => back.focus());
  };

  /* The feed: what fits (or is in), under the filters, six at a time. */
  const list = pool.filter((x) => {
    const q = s.items[x.handle] || 0;
    if (!(q > 0 || fitsIn(rm, x))) return false;
    if (s.cat !== 'all' && !x.cats.includes(s.cat)) return false;
    if (s.diet.includes('vegan') && !x.diet.includes('Vegan')) return false;
    if (s.diet.includes('af') && x.alcohol) return false;
    if (reachOn && !(fitsIn(rm, x) && x.price >= gap)) return false;
    return true;
  });
  const lim = s.all ? list.length : 6;
  /* Anything still goes in: space, or an Extra Small for a full box. */
  const free = rm.free > 0 || pool.some((x) => fitsIn(rm, x));

  const insideRows =
    inside && cfg.contents.length
      ? html`<div class="rm2-inside">
          <h3>${cfg.copy.insideHeading || 'What’s inside'}</h3>
          <ul>
            ${cfg.contents.map(
              (c) => html`<li key=${c.handle}>
                <button type="button" class="rm2-in" data-rm2-ql=${c.handle} aria-haspopup="dialog" aria-label=${`More about ${c.title}`}>
                  <span class="rm2-ph">${c.img ? html`<img src=${c.img} alt="" loading="lazy" />` : null}</span>
                  <span>${c.qty > 1 ? `${c.qty} × ` : ''}${c.title}${c.vendor ? html`<small>${c.vendor}</small>` : null}</span>
                  <${Info} />
                </button>
              </li>`
            )}
          </ul>
        </div>`
      : null;

  return html`
    ${inside ? html`<${Into} node=${inside}>${insideRows}<//>` : null}
    ${points && NS.rewardPoints ? html`<${Into} node=${points}><${Points} points=${NS.rewardPoints(t, cfg)} /><//>` : null}
    ${/* Coming soon (D124): the wraps, the free-delivery bar, the extras and
         Add To Cart give way to the theme's wording, as the first paint does. */ ''}
    ${cfg.comingSoon
      ? html`<div class="rm2-soon-cta" role="status" key="soon">${cfg.soonLabel || 'Coming Soon'}</div>`
      : html`
    <${Wraps} cfg=${cfg} s=${s} set=${set} />
    <${FreeBar} cfg=${cfg} t=${t} rm=${rm} sg=${sg} pending=${!loaded} reach=${reach} />
    ${rm.full ? html`<p class="rm2-full" key="full">${cfg.copy.fullNote}</p>` : null}
    ${rm.full
      ? null
      : html`<div class="rm2-room" key="room">
          <div class="rm2-sub">${cfg.copy.upsellHeading}${cfg.copy.upsellAside ? html`<span>${cfg.copy.upsellAside}</span>` : null}</div>
          <${Chips} cfg=${cfg} s=${s} set=${set} reach=${reachShow} />
          <p class="rm2-feed-n" aria-live="polite">${free ? (reachOn ? COPY.reachN(list.length) : COPY.fits(list.length)) : COPY.nowFull}</p>
        </div>`}
    ${rm.full
      ? null
      : html`<div class="rm2-pcs" key="pcs">
          ${list.slice(0, lim).map(
            (x) => html`<${Card}
              key=${x.handle}
              x=${x}
              q=${s.items[x.handle] || 0}
              fitsMore=${fitsIn(rm, x)}
              onLook=${openLook}
              onAdd=${addFromFeed}
              onDec=${decOne}
            />`
          )}
        </div>`}
    ${!rm.full && list.length > lim
      ? html`<button type="button" class="rm2-morebtn" key="more" onClick=${() => set((x) => ({ ...x, all: true }))}>${COPY.more(list.length - lim)}</button>`
      : null}
    ${!rm.full && !list.length ? html`<p class="rm2-empty" key="empty">${free ? COPY.emptyFree : COPY.emptyFull}</p>` : null}
    <button type="button" class="rm2-btn" key="cta" ref=${cta} data-rm2-cta disabled=${soldOut} onClick=${open}>
      ${soldOut ? COPY.soldOut : html`${cfg.copy.open} · <span>${money(t)}</span>`}
    </button>
    `}
    ${ql
      ? html`<${NS.Layer}>
          <${QuickLook}
            cfg=${cfg}
            ql=${ql}
            s=${s}
            byHandle=${byHandle}
            rm=${rm}
            det=${looks[ql.handle] || null}
            onClose=${closeLook}
            onAdd=${addOne}
            onDec=${decOne}
          />
        <//>`
      : null}
    ${view &&
    html`<${Pop}
      cfg=${cfg}
      s=${s}
      t=${t}
      pool=${pool}
      rm=${rm}
      byHandle=${byHandle}
      view=${view}
      busy=${busy}
      tried=${tried}
      fail=${fail}
      upload=${upload}
      uploadErr=${uploadErr}
      added=${added}
      set=${set}
      onToggleExtra=${toggleExtra}
      onFile=${onFile}
      onAdd=${onAdd}
      onClose=${close}
      onKeepShopping=${keepShopping}
      ask=${ask}
      onAsk=${onAsk}
    />`}
  `;
}

/* ---- the page around the app ------------------------------------------------------- */

/** Thumbnails and arrows swap the main photo; the arrows wrap round. */
function gallery(root) {
  const main = root.querySelector('.rm2-gal img');
  const thumbs = [...root.querySelectorAll('[data-rm2-thumb]')];
  const count = root.querySelector('[data-rm2-gc]');
  if (!main || thumbs.length < 2) return;
  let i = 0;
  const show = (k) => {
    i = (k + thumbs.length) % thumbs.length;
    const t = thumbs[i];
    if (t.dataset.srcset) main.srcset = t.dataset.srcset;
    main.src = t.dataset.src;
    thumbs.forEach((b, j) => {
      b.classList.toggle('is-on', j === i);
      b.setAttribute('aria-current', j === i ? 'true' : 'false');
    });
    if (count) count.textContent = `${i + 1} / ${thumbs.length}`;
  };
  root.querySelector('.rm2-galw').addEventListener('click', (e) => {
    const g = e.target.closest('[data-rm2-g]');
    if (g) return show(i + Number(g.dataset.rm2G));
    const t = e.target.closest('[data-rm2-thumb]');
    if (t) show(Number(t.dataset.rm2Thumb));
    return undefined;
  });
}

/** The rating under the title goes to the box's own reviews (D166), and their
    "See all Wrapt reviews" on to all of them, smoothly unless asked not to. */
function jump(root) {
  root.querySelectorAll('[data-rm2-jump], [data-rm2-jump-all]').forEach((link) => {
    const to = root.querySelector(link.getAttribute('href'));
    if (!to) return;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      to.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    });
  });
}

/** The other boxes' arrows scroll three quarters of the track. */
function others(root) {
  const trk = root.querySelector('[data-rm2-trk]');
  if (!trk) return;
  root.querySelectorAll('[data-rm2-sc]').forEach((b) =>
    b.addEventListener('click', () => trk.scrollBy({ left: Number(b.dataset.rm2Sc) * trk.clientWidth * 0.75, behavior: reducedMotion() ? 'auto' : 'smooth' }))
  );
}

/** "Review for: The OG" -- the name before " | "; the builder's base boxes are
    "Build Your Own", as the design writes it. */
function reviewFor(title) {
  const t = String(title || '').split(' | ')[0].trim();
  if (/^(new byo|byo\b)/i.test(t)) return 'Build Your Own';
  return t;
}

/**
 * Store-wide Judge.me reviews (the design's "What People Say About Wrapt"):
 * Judge.me's own public widget endpoint, the one its review feed reads on this
 * store's pages: its 30 newest reviews, the most it gives. Reviews with a photo
 * come first (D111, Alan), newest first within each. A review running past 320
 * characters is left out while there are three or more shorter ones: the cards
 * share one height, so one long review stretched the whole row. A photo review
 * is kept however long, its words cut before 320 characters.
 */
async function reviews(root, cfg) {
  const sec = root.querySelector('[data-rm2-reviews]');
  const grid = sec && sec.querySelector('[data-rm2-rgrid]');
  if (!grid) return;
  sec.addEventListener('click', (e) => {
    const b = e.target.closest('[data-rm2-rs]');
    if (b) grid.scrollBy({ left: Number(b.dataset.rm2Rs) * grid.clientWidth * 0.7, behavior: reducedMotion() ? 'auto' : 'smooth' });
  });
  const want = (cfg.reviews && cfg.reviews.count) || 12;
  const url = `https://api.judge.me/reviews/reviews_for_review_snippet_widget?v=2&selection_source=all&count=30&min_star_rating=&shop_domain=${encodeURIComponent(cfg.shop)}&platform=shopify`;
  /* One retry after a pause: a refused or failed request (Judge.me rate-limits
     by address) otherwise left the band empty (D125). */
  const get = async () => {
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const json = await res.json();
      return Array.isArray(json.reviews) ? json.reviews : [];
    } catch (_) {
      return null;
    }
  };
  let raw = await get();
  if (raw === null) {
    await new Promise((r) => setTimeout(r, 1500));
    raw = (await get()) || [];
  }
  const parser = typeof DOMParser === 'function' ? new DOMParser() : null;
  const cards = raw
    .map((r) => {
      const doc = parser ? parser.parseFromString(String(r.body_html || ''), 'text/html') : null;
      const text = (doc ? doc.body.textContent : String(r.body_html || '')).replace(/\s+/g, ' ').trim();
      if (!text) return null;
      const pic = Array.isArray(r.pictures_urls) && r.pictures_urls[0];
      const photo = (pic && (pic.compact || pic.original)) || null;
      return {
        key: r.uuid || text.slice(0, 24),
        text,
        rating: Math.max(0, Math.min(5, Math.round(Number(r.rating) || 5))),
        name: String(r.public_reviewer_name || '').trim(),
        verified: !!r.verified_buyer,
        product: reviewFor(r.product_title),
        photo,
      };
    })
    .filter(Boolean);
  const LONG = 320;
  const clip = (text) => {
    const at = text.lastIndexOf(' ', LONG - 1);
    return `${(at > 0 ? text.slice(0, at) : text.slice(0, LONG - 1)).replace(/[\s,;:.!?-]+$/, '')}…`;
  };
  const shortN = cards.filter((c) => c.text.length <= LONG).length;
  const kept = cards
    .filter((c) => c.photo || c.text.length <= LONG || shortN < 3)
    .map((c) => (c.photo && c.text.length > LONG ? { ...c, text: clip(c.text) } : c));
  const pick = [...kept.filter((c) => c.photo), ...kept.filter((c) => !c.photo)].slice(0, want);
  if (!pick.length) {
    sec.querySelector('.rm2-trk-w').hidden = true;
    const arr = sec.querySelector('.rm2-arr');
    if (arr) arr.hidden = true;
    return;
  }
  render(
    pick.map(
      (c) => html`<div class="rm2-rc" key=${c.key}>
        ${c.photo ? html`<img class="rm2-rc-ph" src=${c.photo} alt="" loading="lazy" />` : null}
        <div class="rm2-st" role="img" aria-label=${`Rated ${c.rating} out of 5`}>${'★'.repeat(c.rating)}${'☆'.repeat(5 - c.rating)}</div>
        <p>${c.text}</p>
        <div class="rm2-who">${c.name}${c.verified ? ' ✓' : ''}</div>
        ${c.product ? html`<div class="rm2-for">Review for: ${c.product}</div>` : null}
      </div>`
    ),
    grid
  );
}

/** "Order in 2h 14m", "tomorrow", "Saturday morning", "Monday". */
function fmtRemaining(seconds) {
  const minutes = Math.ceil(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/**
 * The delivery list's first line, in the design's words, with the time or the
 * day in bold. The clock is the theme's own dispatch element (the top bar's),
 * so the cut-off, Friday's Saturday dispatch and the bank holidays are its
 * rules, not a second copy of them; it stays in the markup, hidden, and this
 * reads its state every 30 seconds.
 */
function dispatchLine(root, cfg) {
  const holder = root.querySelector('[data-rm2-dispatch]');
  const el = holder && holder.querySelector('wrapt-dispatch-promise');
  if (!holder || !el || !window.customElements) return;
  const out = document.createElement('span');
  out.className = 'rm2-dl__line';
  const paint = () => {
    const st = typeof el.state === 'function' ? el.state() : null;
    if (!st) return;
    let tpl;
    let val;
    if (st.name === 'today') {
      tpl = cfg.copy.dlToday || '';
      val = fmtRemaining(st.remaining);
    } else {
      tpl = cfg.copy.dlLater || '';
      const phrase = typeof el.shipPhrase === 'function' ? el.shipPhrase(st) : '';
      val = phrase.replace(/^ships\s+/i, '') || (st.name === 'tomorrow' ? 'tomorrow' : st.day || '');
    }
    const token = st.name === 'today' ? '{time}' : '{day}';
    const parts = tpl.split(token);
    if (!tpl || parts.length < 2 || !val) return;
    out.textContent = '';
    out.appendChild(document.createTextNode(parts[0]));
    const b = document.createElement('b');
    b.textContent = val;
    out.appendChild(b);
    out.appendChild(document.createTextNode(parts.slice(1).join(token)));
    if (!out.isConnected) {
      el.hidden = true;
      holder.appendChild(out);
    }
  };
  window.customElements.whenDefined('wrapt-dispatch-promise').then(() => {
    paint();
    setInterval(paint, 30000);
  });
}

/**
 * Points — the Wrapt Rewards card under the delivery callout (D119), in the
 * builder's PointsCard wording. It is a button: Smile's launcher is hidden on
 * this page and the card opens the Rewards panel instead (D120, Alan).
 */
function Points({ points }) {
  if (!points || !NS.Icon) return null;
  return html`<button type="button" class="byo-v4-points" data-rm2-rewards aria-haspopup="dialog">
    <${NS.Icon} name="star" size=${18} />
    <span><strong>Earn ${points.toLocaleString('en-GB')} points</strong> with this box to spend on <span class="rm2-pts-l">Wrapt Rewards</span>.</span>
  </button>`;
}

/** Where the points card goes when Smile cannot open its panel. */
const REWARDS_URL = '/pages/rewards';

/**
 * openRewards — Smile's Rewards panel without its launcher. The page loads
 * Smile "lite" (the launcher alone); its SDK, and with it `SmileUI.openPanel`,
 * loads on `loadSmileSdk()`, which the launcher itself calls on a tap.
 * Measured on the live theme, 1 Oct 2026: the panel opens with the launcher
 * hidden. Failing that, the launcher's own button; failing that, the page.
 */
async function openRewards(btn) {
  const ui = () => (window.SmileUI && typeof window.SmileUI.openPanel === 'function' ? window.SmileUI : null);
  /* Smile now loads once the page is idle (D136): a tap before then starts it. */
  if (!ui() && typeof window.loadSmileSdk !== 'function' && typeof window.wraptLoadSmile === 'function') {
    btn.setAttribute('aria-busy', 'true');
    await window.wraptLoadSmile();
    for (let i = 0; i < 40 && !ui() && typeof window.loadSmileSdk !== 'function'; i += 1) await new Promise((r) => setTimeout(r, 100));
    btn.removeAttribute('aria-busy');
  }
  if (!ui() && typeof window.loadSmileSdk === 'function') {
    btn.setAttribute('aria-busy', 'true');
    try {
      window.loadSmileSdk();
    } catch (_) {
      /* the fallbacks follow */
    }
    for (let i = 0; i < 40 && !ui(); i += 1) await new Promise((r) => setTimeout(r, 100));
    btn.removeAttribute('aria-busy');
  }
  if (ui()) {
    try {
      ui().openPanel();
      return;
    } catch (_) {
      /* the fallbacks follow */
    }
  }
  let own = null;
  try {
    const f = document.getElementById('smile-lite-launcher-frame');
    own = f && f.contentDocument && f.contentDocument.querySelector('button');
  } catch (_) {
    own = null;
  }
  if (own) own.click();
  else window.location.href = REWARDS_URL;
}

/** The points card, first paint or app, opens Rewards. */
function rewards(root) {
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rm2-rewards]');
    if (!btn || btn.getAttribute('aria-busy')) return;
    e.preventDefault();
    openRewards(btn);
  });
}

/**
 * The chat line under the button (it replaced D120's floating Help button on
 * 24 Oct 2026) opens the Shopify Inbox chat, whose own bubble is
 * hidden here (D120, Alan: "embed the chat function behind a little help
 * icon"). `show()` is the component's dialog-style opener, as the builder's
 * showChat uses; when Inbox is off or held back by consent, the link goes to
 * the contact page.
 */
function help(root) {
  const link = root.querySelector('[data-rm2-help]');
  if (!link) return;
  link.addEventListener('click', (e) => {
    const chat = document.querySelector('shopify-chat');
    if (!chat || typeof chat.show !== 'function') return;
    try {
      chat.show();
      e.preventDefault();
    } catch (_) {
      /* the link goes to the contact page */
    }
  });
}

/* D120's link rewrite (every link to the Build Your Own page sent to #build,
   the menu's included) is retired by D144: the page's own Build Your Own
   buttons go to the builder's page, and the menu goes to the landing. */

/** Mount over the section's server-drawn first paint. */
export function mount(root) {
  const el = root && root.querySelector('[data-rm2-config]');
  const buy = root && root.querySelector('[data-rm2-buy]');
  if (!el || !buy) return false;
  let cfg;
  try {
    cfg = JSON.parse(el.textContent);
  } catch (e) {
    console.error('[wrapt-ready-made-v2] the page config did not parse', e);
    return false;
  }
  cfg.addons = cfg.addons || [];
  cfg.contents = cfg.contents || [];
  cfg.chips = cfg.chips || [];
  const inside = cfg.rules.linked ? root.querySelector('[data-rm2-inside]') : null;
  buy.textContent = '';
  if (inside) inside.textContent = '';
  /* The rewards card under the delivery callout follows the page's total (D119). */
  const points = root.querySelector('[data-rm2-points]');
  if (points) points.textContent = '';
  render(html`<${App} cfg=${cfg} root=${root} inside=${inside} points=${points} />`, buy);
  if (!root.dataset.rm2Wired) {
    root.dataset.rm2Wired = '1';
    gallery(root);
    jump(root);
    others(root);
    reviews(root, cfg);
    dispatchLine(root, cfg);
    rewards(root);
    help(root);
  }
  root.classList.add('is-ready');
  return true;
}

/** Take the app down again (the QA driver remounts it with test data). */
export function unmount(root) {
  const buy = root && root.querySelector('[data-rm2-buy]');
  if (buy) render(null, buy);
  if (root) root.classList.remove('is-ready');
}

/* For the QA driver: the rules and the payload can be asserted on without a cart. */
window.WraptReadyMade2 = { KEYS, bestSet, blankBox, canReach, buildLine, checks, chosen, formOf, mergePool, money, room, sizePill, total, mount, unmount };
